import React, { useCallback } from 'react';
import classnames from 'classnames';
import { Icon } from 'js/components';
import { noop, map, mean } from 'lodash';
import RadialPieChart from 'js/components/Charts/RadialPieChart';
import DetailData from './DetailData';

import styles from '../DetailsPane.module.scss';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';

export const QuestionSetDetailPane = ({
  summaryData,
  filter,
  categoryColorMap,
  paletteMap,
  questionSetKey,
  onSliceOver = noop,
  onSliceOut = noop,
  onSliceClick = noop,
  generateKey = (questionSetKey, questionKey) =>
    `${questionSetKey}-${questionKey}`,
}) => {
  const questionSet = summaryData.questionSets[questionSetKey];
  const dataPoints = map(questionSet.questions, (q, qKey) => {
    return {
      ...q,
      category: questionSet.category,
      questionSetKey: questionSet.key,
      questionKey: qKey,
      paletteKey: generateKey(questionSet.key, qKey),
    };
  });

  const paletteClassName = useCallback(
    ({ questionKey, questionSetKey, paletteKey }) => {
      return classnames(
        paletteMap[paletteKey]?.className,
        filter?.criteria?.questionKey === questionKey &&
          filter?.criteria?.questionSetKey === questionSetKey
          ? undefined
          : 'palette-muted'
      );
    },
    [paletteMap, filter]
  );

  const handleHoverSlice = useCallback(
    ({ questionSetKey, questionKey }) => {
      onSliceOver(questionSetKey, questionKey);
    },
    [onSliceOver]
  );

  const handleClearQuestionSetFilter = useCallback(() => {
    onSliceClick(null);
  }, [onSliceClick]);

  const handleClearSlices = useCallback(() => {
    onSliceOut();
  }, [onSliceOut]);

  const getDetailData = () => {
    if (filter?.subtype === 'question') {
      const question =
        summaryData.questionSets[filter.criteria.questionSetKey].questions[
          filter.criteria.questionKey
        ];
      const normalizationFactor =
        question.answerDomain[1] - question.answerDomain[0];
      const responses = Object.keys(question.responseTotals)
        .map((key) => parseInt(key, 10))
        .sort()
        .flatMap((key) => new Array(question.responseTotals[key]).fill(key));
      const normalizedMedianScore =
        responses.length > 0
          ? (responses[Math.floor((responses.length - 1) / 2)] +
              responses[Math.ceil((responses.length - 1) / 2)]) /
            (2 * normalizationFactor)
          : null;

      return {
        titleLabel: 'Question',
        title: question.text,
        meanScore: question.normalizedScore,
        medianScore: normalizedMedianScore,
        stdDev: Math.sqrt(question.normalizedVariance),
      };
    }

    // else
    const scores = dataPoints
      .map((d) => d.normalizedScore)
      .filter((x) => x !== null);
    const meanScore = scores.length > 0 ? mean(scores) : null;
    const medianScore =
      scores.length > 0
        ? (scores[Math.floor((scores.length - 1) / 2)] +
            scores[Math.ceil((scores.length - 1) / 2)]) /
          2
        : null;
    const variance =
      scores.length > 1
        ? mean(scores.map((s) => Math.pow(s - meanScore, 2)))
        : null;

    return {
      title: questionSet.displayName,
      meanScore,
      medianScore,
      stdDev: Math.sqrt(variance),
    };
  };

  const detailProps = getDetailData();

  return (
    <div>
      <div className={styles['overview-header']}>
        <div>
          <label>Statistics</label>
        </div>
        <div>
          <button
            className="btn btn-link btn-info m-0"
            onClick={handleClearQuestionSetFilter}
          >
            <Icon icon={faChevronLeft} className="icon-left" size="sm" />
            Overview
          </button>
        </div>
      </div>
      <div className={styles['overview-data']}>
        <div className={styles['overview-chart']}>
          <div className="assessment-history-chart">
            <div className="assessment-history-circle-chart">
              <div>
                <RadialPieChart
                  dataPoints={dataPoints}
                  colorMap={categoryColorMap ?? {}}
                  onHoverSlice={handleHoverSlice}
                  onClearSlices={handleClearSlices}
                  paletteClassName={paletteClassName}
                />
              </div>
            </div>
          </div>
        </div>
        <DetailData {...detailProps} />
      </div>
    </div>
  );
};

export default QuestionSetDetailPane;
